import * as authService from './authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

const BASE = '/api/v1/ber-thong'

export type BerThongBetType = 'top2' | 'bottom2'

export type BerThongSheet = {
  visualThemeId?: string
  id: string
  sheetCode?: string
  title: string
  pricePerSlot: number
  betType?: BerThongBetType
  prizeAmount: number
  prizeLabel: string
  drawDate: string
  huayName: string
  huayId?: string
  roundId?: string
  closeAt?: string | null
  openAt?: string | null
  status: string
  soldCount?: number
  availableCount?: number
  totalSlots?: number
  cancelIfBelowEnabled?: boolean
  minSoldToKeep?: number | null
  cancelledAt?: string | null
  cancelReason?: string
  winningTop2?: string
  winningBottom2?: string
  winningNumber?: string
}

export type BerThongSlot = {
  id: string
  number: string
  status: 'available' | 'sold'
  betType?: BerThongBetType | ''
  buyerUsername?: string
  isWinner?: boolean
}

export type BerThongPurchase = {
  id: string
  sheetId: string
  sheetCode?: string
  sheetTitle: string
  huayName?: string
  drawDate: string
  betType?: BerThongBetType
  winningTop2?: string
  winningBottom2?: string
  winningNumber: string
  sheetStatus: string
  numbers: string[]
  totalAmount: number
  unitPrice: number
  status: string
  createdAt: string
  hasReceiptImage?: boolean
  receiptUrl?: string
}

function headers(): HeadersInit {
  const token = authService.getToken()
  const tenantId = getRuntimeTenantIdSync()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const message =
      (data as { message?: string; error?: string })?.message
      || (data as { error?: string })?.error
      || `HTTP ${response.status}`
    const err = new Error(message) as Error & { code?: string; status?: number }
    err.code = (data as { code?: string })?.code
    err.status = response.status
    throw err
  }
  return data as T
}

export async function fetchBerThongPlayConfig() {
  const response = await fetch(withApiBaseUrl(`${BASE}/play-config`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data?: {
      userEntryVisible: boolean
      sheets: BerThongSheet[]
    }
    message?: string
  }>(response)
}

export async function fetchBerThongSheet(sheetId: string) {
  const response = await fetch(withApiBaseUrl(`${BASE}/sheets/${sheetId}`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data?: { sheet: BerThongSheet; slots: BerThongSlot[] }
    message?: string
  }>(response)
}

export async function purchaseBerThongSlots(sheetId: string, numbers: string[]) {
  const response = await fetch(withApiBaseUrl(`${BASE}/sheets/${sheetId}/purchase`), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ numbers }),
  })
  return parseJson<{
    success: boolean
    data?: {
      purchase: {
        id: string
        numbers: string[]
        betType?: BerThongBetType
        totalAmount: number
        unitPrice: number
        sheetCode?: string
        sheetTitle?: string
        huayName?: string
        drawDate?: string
        createdAt?: string
      }
      newBalance?: number | null
    }
    message?: string
  }>(response)
}

export async function fetchMyBerThongPurchases(limit = 50) {
  const response = await fetch(withApiBaseUrl(`${BASE}/purchases/my?limit=${limit}`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data?: BerThongPurchase[]
    message?: string
  }>(response)
}

export async function uploadBerThongReceipt(purchaseId: string, imageBase64: string, contentType = 'image/png') {
  const response = await fetch(withApiBaseUrl(`${BASE}/purchases/${purchaseId}/receipt`), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ imageBase64, contentType }),
  })
  return parseJson<{
    success: boolean
    data?: BerThongPurchase
    message?: string
  }>(response)
}

export async function fetchBerThongReceipt(purchaseId: string) {
  const response = await fetch(withApiBaseUrl(`${BASE}/purchases/${purchaseId}/receipt`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data?: BerThongPurchase
    message?: string
  }>(response)
}
